import { useState, useEffect, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  getOpDef,
  listOpsByFamily,
  validateTransformChain,
  type TransformOpDef,
  type TransformFamily,
} from '@capture/shared';
import { TransformPreviewPane } from './TransformPreviewPane';
import { TransformStepConfig } from './TransformStepConfig';
import { SaveAsNamedDialog } from './SaveAsNamedDialog';
import { usePublishedTransformPlugins } from '../hooks/usePublishedTransformPlugins';
import { groupPluginOpsByFamily, filterPluginOps, type PluginPickerOp } from '../lib/plugin-ops';
import type { TransformPluginRow } from '@/features/admin/transform-plugins/api/transform-plugins-client';
import styles from './TransformChainDrawer.module.css';

interface ChainStep {
  type: string;
  config: Record<string, unknown>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  chain: ChainStep[];
  sourceFieldName: string;
  targetColumnName: string;
  contextFields: Record<string, unknown>;
  onSave: (chain: ChainStep[]) => void;
  /**
   * Optional callback invoked after the operator saves the current inline steps as a
   * new named chain. Receives the newly created named-chain ID. MappingPanel uses this
   * to flip the mapping to `{ kind: 'named', namedChainId }`.
   */
  onSavedAsNamed?: (namedChainId: string) => void;
  /**
   * Optional callback invoked when the operator clicks "Promote to canonical".
   * Receives the current draft chain. The parent (MappingPanel) handles the
   * named-chain auto-resolve, dialog rendering, and FormConfig draft mutation.
   */
  onPromoteToCanonical?: (steps: ChainStep[]) => void;
}

export function TransformChainDrawer({
  open,
  onClose,
  chain: initialChain,
  sourceFieldName,
  targetColumnName,
  contextFields,
  onSave,
  onSavedAsNamed,
  onPromoteToCanonical,
}: Props) {
  const [draft, setDraft] = useState<ChainStep[]>(initialChain);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const { plugins } = usePublishedTransformPlugins();

  // Reset draft when the drawer reopens with a new chain
  useEffect(() => {
    if (open) setDraft(initialChain);
  }, [open, initialChain]);

  const pluginOpIds = useMemo(() => new Set(plugins.map((p) => p.pluginId)), [plugins]);
  const validation = useMemo(
    () => validateTransformChain(draft, { knownExternalOpIds: pluginOpIds }),
    [draft, pluginOpIds],
  );
  const errorsByStep = useMemo(() => {
    const map: Record<number, Array<{ field: string; message: string }>> = {};
    if (!validation.ok) {
      for (const e of validation.errors) {
        (map[e.stepIndex] ??= []).push({ field: e.field, message: e.message });
      }
    }
    return map;
  }, [validation]);

  const seed = useMemo(() => {
    const def = getOpDef(draft[0]?.type ?? '');
    return def?.sampleSeeds[0] ?? '2026-04-15';
  }, [draft]);

  function updateStep(i: number, patch: Partial<ChainStep>) {
    setDraft(draft.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  }

  function removeStep(i: number) {
    setDraft(draft.filter((_, j) => j !== i));
  }

  function moveStep(from: number, to: number) {
    if (to < 0 || to >= draft.length) return;
    const next = [...draft];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    setDraft(next);
  }

  function addOp(opDef: TransformOpDef | PluginPickerOp) {
    setDraft([...draft, { type: opDef.id, config: { ...opDef.defaultConfig } }]);
    setPickerOpen(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>
              Transform chain — {sourceFieldName} → {targetColumnName}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className={styles.closeBtn} aria-label="Close">
                ✕
              </button>
            </Dialog.Close>
          </header>

          <section className={styles.previewSection}>
            <TransformPreviewPane
              seed={seed}
              chain={draft}
              contextFields={contextFields}
              plugins={plugins}
            />
          </section>

          <section className={styles.chainSection}>
            {draft.length === 0 && (
              <p className={styles.empty}>No transforms yet. Click &quot;Add transform&quot; to start.</p>
            )}
            {draft.map((step, i) => (
              <div key={`${step.type}-${i}`} className={styles.stepCard}>
                <header className={styles.stepHeader}>
                  <span className={styles.stepLabel}>
                    Step {i + 1} — {step.type}
                  </span>
                  <div className={styles.stepActions}>
                    <button
                      type="button"
                      aria-label="Move up"
                      onClick={() => moveStep(i, i - 1)}
                      disabled={i === 0}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label="Move down"
                      onClick={() => moveStep(i, i + 1)}
                      disabled={i === draft.length - 1}
                    >
                      ↓
                    </button>
                    <button type="button" aria-label="Remove" onClick={() => removeStep(i)}>
                      ×
                    </button>
                  </div>
                </header>
                <TransformStepConfig
                  opId={step.type}
                  config={step.config}
                  onChange={(config) => updateStep(i, { config })}
                  errors={errorsByStep[i] ?? []}
                  plugins={plugins}
                />
              </div>
            ))}

            <div className={styles.addOpRow}>
              {pickerOpen ? (
                <OpPicker onPick={addOp} onCancel={() => setPickerOpen(false)} plugins={plugins} />
              ) : (
                <button type="button" onClick={() => setPickerOpen(true)}>
                  + Add transform
                </button>
              )}
            </div>
          </section>

          <details className={styles.threeTier}>
            <summary>About destination-specific transforms (three-tier model)</summary>
            <p>
              This chain affects only THIS export destination. For tweaks every consumer should
              see (e.g. always strip a prefix from the canonical field value), configure a
              Post-OCR transform on the field instead. The three tiers in order are:{' '}
              <strong>Post-OCR</strong> (canonical), <strong>Pre-Export compute</strong> (derived
              per batch), <strong>Mapping-row transforms</strong> (this drawer —
              destination-specific).
            </p>
          </details>

          <footer className={styles.footer}>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            {onSavedAsNamed && draft.length > 0 && (
              <button
                type="button"
                onClick={() => setSaveAsDialogOpen(true)}
                style={{ marginRight: 'auto' }}
              >
                Save as named chain
              </button>
            )}
            {onPromoteToCanonical && draft.length > 0 && (
              <button
                type="button"
                onClick={() => onPromoteToCanonical(draft)}
                style={{ marginRight: 'auto' }}
              >
                ↑ Promote to canonical
              </button>
            )}
            <button type="button" disabled={!validation.ok} onClick={() => onSave(draft)}>
              Save &amp; close
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>

      {onSavedAsNamed && (
        <SaveAsNamedDialog
          open={saveAsDialogOpen}
          steps={draft}
          onClose={() => setSaveAsDialogOpen(false)}
          onCreated={(id) => {
            setSaveAsDialogOpen(false);
            onSavedAsNamed(id);
          }}
        />
      )}
    </Dialog.Root>
  );
}

function OpPicker({
  onPick,
  onCancel,
  plugins = [],
}: {
  onPick: (op: TransformOpDef | PluginPickerOp) => void;
  onCancel: () => void;
  plugins?: TransformPluginRow[];
}) {
  const [search, setSearch] = useState('');
  const families = listOpsByFamily();
  const familyOrder: TransformFamily[] = [
    'string',
    'literal',
    'cross-field',
    'logic',
    'lookup',
    'number',
    'date',
  ];
  const familyLabel: Record<TransformFamily, string> = {
    string: 'String ops',
    literal: 'Literal ops',
    'cross-field': 'Cross-field',
    logic: 'Logic',
    lookup: 'Lookup',
    number: 'Number',
    date: 'Date',
  };

  // F-043 — merge published tenant plugins into their declared family group,
  // alongside the static built-ins, badged "custom" so operators can tell
  // them apart at a glance. Memoized on `plugins` (not `search`) so it isn't
  // rebuilt on every keystroke in the search box.
  const pluginsByFamily = useMemo(() => groupPluginOpsByFamily(plugins), [plugins]);

  return (
    <div className={styles.picker}>
      <input
        autoFocus
        type="search"
        placeholder="Search transforms…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <button type="button" onClick={onCancel}>
        Cancel
      </button>
      <ul className={styles.pickerList}>
        {familyOrder.map((fam) => {
          const ops = (families[fam] ?? []).filter(
            (op) =>
              !search ||
              op.id.includes(search.toLowerCase()) ||
              op.label.toLowerCase().includes(search.toLowerCase()),
          );
          const pluginOps = filterPluginOps(pluginsByFamily[fam] ?? [], search);
          if (ops.length === 0 && pluginOps.length === 0) return null;
          return (
            <li key={fam}>
              <div className={styles.pickerFamily}>{familyLabel[fam]}</div>
              {ops.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  role="option"
                  className={styles.pickerOp}
                  onClick={() => onPick(op)}
                >
                  <span>{op.label}</span>
                  <span className={styles.pickerDescription}>{op.description}</span>
                </button>
              ))}
              {pluginOps.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  role="option"
                  aria-label={`${op.label} (custom)`}
                  className={styles.pickerOp}
                  onClick={() => onPick(op)}
                >
                  <span>
                    {op.label} <span className={styles.pickerBadge}>custom</span>
                  </span>
                  <span className={styles.pickerDescription}>{op.description}</span>
                </button>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
